import { Colors, Metrics, Typography } from "../../resources";
import {
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import React, { useMemo, useState } from "react";
import { SIconType, STagType } from "../../types";

import { Analytics } from "../../library";
import SIcon from "./SIcon";
import { useTags } from "../../hooks";

interface IProps {
  id: string;
  title: string;
  type?: STagType;
  number?: number;
  isSelected?: boolean;
  shared?: boolean;
  more?: boolean;
  onClick: () => void;
  onLayout: (id: string, e: any) => void;
}

const STag: React.FC<IProps> = ({
  id,
  title,
  type = STagType.Plain,
  more = false,
  number = 0,
  isSelected = false,
  shared = true,
  onClick,
  onLayout,
}) => {
  const [hover, setHover] = useState<boolean>(false);
  const [pressIn, setPressIn] = useState<boolean>(false);

  const { setSelected, setUnselected } = useTags();

  const onHoverIn = () => setHover(true);
  const onHoverOut = () => setHover(false);
  const onPressIn = () => setPressIn(true);
  const onPressOut = () => setPressIn(false);

  const onPress = () => {
    setUnselected(id);
    onClick();
  };

  const renderIcon = () => {
    switch (type) {
      case STagType.Edited: {
        return (
          <Pressable
            style={[
              styles.iconContainer,
              hover && styles.hover,
              pressIn && styles.pressIn,
            ]}
            onPress={onPress}
            onMouseEnter={onHoverIn}
            onMouseLeave={onHoverOut}
            onPressIn={onPressIn}
            onPressOut={onPressOut}
          >
            <SIcon
              type={SIconType.Closed}
              color={Colors.midnight40}
              size={Metrics.size5}
            />
          </Pressable>
        );
      }
      case STagType.Closed: {
        return (
          <Pressable
            style={[
              styles.iconContainer,
              hover && styles.hover,
              pressIn && styles.pressIn,
            ]}
            onPress={onPress}
            onMouseEnter={onHoverIn}
            onMouseLeave={onHoverOut}
            onPressIn={onPressIn}
            onPressOut={onPressOut}
          >
            <SIcon
              type={SIconType.Closed}
              color={Colors.midnight40}
              size={Metrics.size5}
            />
          </Pressable>
        );
      }
      case STagType.Numbered: {
        return (
          <View
            style={[
              styles.number,
              {
                backgroundColor: isSelected ? Colors.white80 : Colors.white,
              },
            ]}
          >
            <Text style={styles.numberText}>{number}</Text>
          </View>
        );
      }
      case STagType.Choice: {
        return (
          <TouchableOpacity style={styles.iconContainer} onPress={onPress}>
            <SIcon type={SIconType.ChevronDown} color={Colors.blue700} />
          </TouchableOpacity>
        );
      }
      default: {
        return more ? (
          <View style={styles.chevronContainer}>
            <SIcon
              type={SIconType.ChevronDown}
              color={Colors.blue700}
              size={12}
            />
          </View>
        ) : null;
      }
    }
  };

  let backgroundColor = Colors.blue100;
  let color = Colors.blue700;
  switch (type) {
    case STagType.Closed: {
      backgroundColor = Colors.grey100;
      color = Colors.grey700;
      break;
    }
    case STagType.Numbered: {
      let selectedBgColor = isSelected ? Colors.blue400 : Colors.blue000;
      let selectedColor = isSelected ? Colors.white : Colors.blue700;
      backgroundColor = number === 0 ? Colors.grey100 : selectedBgColor;
      color = number === 0 ? Colors.grey400 : selectedColor;
      break;
    }
    default: {
      backgroundColor = Colors.blue100;
      color = Colors.blue700;
      break;
    }
  }

  const borderColor = useMemo(() => {
    if (type !== STagType.Numbered || (isSelected && number !== 0)) {
      return Colors.transparent;
    }
    return number === 0 ? Colors.grey400 : Colors.blue300;
  }, [type, isSelected, number]);

  const onToggle = () => {
    if (!isSelected) {
      Analytics.track("Portfolio Filter", { value: id });
    }
    isSelected ? setUnselected(id) : setSelected(id);
  };

  return (
    <TouchableOpacity
      style={[
        styles.container,
        {
          backgroundColor: backgroundColor,
          borderWidth: Metrics.size1,
          borderColor: borderColor,
          opacity: !shared && type == STagType.Edited ? 0.5 : 1,
        },
      ]}
      disabled={
        type === STagType.Edited ||
        type === STagType.Closed ||
        type === STagType.Plain ||
        (number === 0 && type === STagType.Numbered)
      }
      onPress={onToggle}
      onLayout={(e) => onLayout(id, e)}
    >
      <Text style={[styles.text, { color: color }]}>{title}</Text>
      {renderIcon()}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    height: Metrics.iconSmall,
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: Metrics.padding8,
    paddingRight: Metrics.paddingSuperSmall,
    borderRadius: Metrics.iconSmall / 2,
    marginVertical: 1,
  },
  number: {
    height: Metrics.iconSuperSmall,
    borderRadius: Metrics.iconSuperSmall / 2,
    justifyContent: "center",
    alignItems: "center",
  },
  text: {
    ...Typography.small,
    paddingRight: Metrics.paddingSuperSmall,
    textAlign: "center",
    textAlignVertical: "center",
    paddingBottom: 2,
  },
  numberText: {
    ...Typography.tag,
    paddingRight: Metrics.paddingLudicrousSmall,
    paddingLeft: Metrics.paddingLudicrousSmall,
    textAlign: "center",
    textAlignVertical: "center",
  },
  iconContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: Metrics.paddingSuperSmall,
    borderRadius: (Metrics.size5 + 2 * Metrics.paddingSuperSmall) / 2,
  },
  chevronContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  hover: {
    backgroundColor: Colors.white,
  },
  pressIn: {},
});

export default STag;
